import { z } from "zod";
// COORDINATION REQUIRED: LC-96 must not edit this shared book-generation contract
// without coordinating with the Comic/Recipe pipeline owner.
const ulidSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const bookGenerationProviderSchema = z.enum([
    "ANTHROPIC",
    "OPENAI",
    "AWS",
    "ELEVENLABS",
    "LIFESCAPSULE",
]);
export const bookGenerationCostEvidenceSourceSchema = z.enum([
    "PROVIDER_REPORTED_USAGE",
    "FALLBACK_ESTIMATE",
    "NO_PROVIDER_COST",
]);
export const bookGenerationSourceBindingV1Schema = z
    .object({
    version: z.literal(1),
    bookRevision: z.number().int().nonnegative(),
    contentRevision: z.number().int().nonnegative(),
    sourceHashSha256: sha256Schema,
})
    .strict();
export const bookGenerationPromptBindingV1Schema = z
    .object({
    version: z.literal(1),
    promptId: z.string().min(1).max(160),
    promptVersion: z.string().min(1).max(100),
    promptHashSha256: sha256Schema,
    schemaId: z.string().min(1).max(160),
    schemaVersion: z.string().min(1).max(100),
    schemaHashSha256: sha256Schema,
})
    .strict();
export const bookGenerationProviderCallV1Schema = z
    .object({
    version: z.literal(1),
    provider: bookGenerationProviderSchema,
    modelId: z.string().min(1).max(500),
    providerRequestId: z.string().min(1).max(500),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    actualCostMicros: z.number().int().nonnegative(),
    costEvidenceSource: bookGenerationCostEvidenceSourceSchema,
    pricingVersion: z.string().min(1).max(100),
    purpose: z.enum(["PRIMARY", "FALLBACK", "SCHEMA_REPAIR"]),
})
    .strict();
export const bookGenerationUsageV1Schema = z
    .object({
    version: z.literal(1),
    calls: z.array(bookGenerationProviderCallV1Schema).min(1).max(10),
    fallbackUsed: z.boolean(),
    schemaRepairAttempts: z.number().int().nonnegative().max(3),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    actualCostMicros: z.number().int().nonnegative(),
})
    .strict()
    .superRefine((usage, context) => {
    const inputTokens = usage.calls.reduce((total, call) => total + call.inputTokens, 0);
    const outputTokens = usage.calls.reduce((total, call) => total + call.outputTokens, 0);
    const actualCostMicros = usage.calls.reduce((total, call) => total + call.actualCostMicros, 0);
    if (usage.inputTokens !== inputTokens)
        context.addIssue({
            code: "custom",
            path: ["inputTokens"],
            message: "Aggregate input tokens must equal the recorded provider calls.",
        });
    if (usage.outputTokens !== outputTokens)
        context.addIssue({
            code: "custom",
            path: ["outputTokens"],
            message: "Aggregate output tokens must equal the recorded provider calls.",
        });
    if (usage.actualCostMicros !== actualCostMicros)
        context.addIssue({
            code: "custom",
            path: ["actualCostMicros"],
            message: "Aggregate cost must equal the recorded provider calls.",
        });
});
export const bookGenerationRetryLineageV1Schema = z
    .object({
    version: z.literal(1),
    rootJobId: ulidSchema,
    attempt: z.number().int().nonnegative().max(3),
    retryOfJobId: ulidSchema.optional(),
})
    .strict();
const bookGenerationProviderSpendV1Schema = z
    .object({
    estimatedCostMicros: z.number().int().nonnegative(),
    reservedCostMicros: z.number().int().nonnegative(),
    actualCostMicros: z.number().int().nonnegative(),
})
    .strict();
export const bookGenerationBudgetReservationV1Schema = z
    .object({
    version: z.literal(1),
    id: ulidSchema,
    jobId: ulidSchema,
    action: z.string().min(1).max(100),
    provider: bookGenerationProviderSchema,
    estimatedCostMicros: z.number().int().nonnegative(),
    maximumCostMicros: z.number().int().positive(),
    actualCostMicros: z.number().int().nonnegative().optional(),
    mandatory: z.boolean(),
    state: z.enum(["RESERVED", "RECONCILED", "RELEASED"]),
    createdAt: z.string().datetime(),
    reconciledAt: z.string().datetime().optional(),
})
    .strict();
export const bookGenerationBudgetV1Schema = z
    .object({
    version: z.literal(1),
    bookId: ulidSchema,
    productionFamily: z.enum(["NARRATIVE", "RECIPE", "COMIC_CORE", "PICTURE_BOOK"]),
    policyId: z.string().min(1).max(160),
    policyVersion: z.string().min(1).max(100),
    revision: z.number().int().nonnegative(),
    dollarLimitMicros: z.number().int().positive().max(30_000_000),
    estimatedCostMicros: z.number().int().nonnegative(),
    reservedCostMicros: z.number().int().nonnegative(),
    actualCostMicros: z.number().int().nonnegative(),
    remainingCostMicros: z.number().int().nonnegative(),
    protectedReserveMicros: z.number().int().nonnegative(),
    protectedReserveProvider: bookGenerationProviderSchema.optional(),
    providerBreakdown: z.record(z.string().min(1).max(40), bookGenerationProviderSpendV1Schema),
    reservations: z.array(bookGenerationBudgetReservationV1Schema).max(100),
    exhaustionState: z.enum(["ACTIVE", "DOLLAR_EXHAUSTED", "COMPLETE"]),
    updatedAt: z.string().datetime(),
})
    .strict()
    .superRefine((budget, context) => {
    if (budget.actualCostMicros + budget.reservedCostMicros + budget.remainingCostMicros !==
        budget.dollarLimitMicros)
        context.addIssue({
            code: "custom",
            path: ["remainingCostMicros"],
            message: "Actual, reserved, and remaining spend must equal the hard dollar limit.",
        });
    for (const provider of Object.keys(budget.providerBreakdown))
        if (!bookGenerationProviderSchema.safeParse(provider).success)
            context.addIssue({
                code: "custom",
                path: ["providerBreakdown", provider],
                message: "Provider breakdown contains an unsupported provider.",
            });
    const providerSpend = Object.values(budget.providerBreakdown).reduce((totals, spend) => ({
        estimatedCostMicros: totals.estimatedCostMicros + spend.estimatedCostMicros,
        reservedCostMicros: totals.reservedCostMicros + spend.reservedCostMicros,
        actualCostMicros: totals.actualCostMicros + spend.actualCostMicros,
    }), { estimatedCostMicros: 0, reservedCostMicros: 0, actualCostMicros: 0 });
    for (const field of ["estimatedCostMicros", "reservedCostMicros", "actualCostMicros"])
        if (providerSpend[field] !== budget[field])
            context.addIssue({
                code: "custom",
                path: ["providerBreakdown"],
                message: `Provider ${field} must equal the aggregate budget value.`,
            });
    if (budget.protectedReserveMicros > 0 && !budget.protectedReserveProvider)
        context.addIssue({
            code: "custom",
            path: ["protectedReserveProvider"],
            message: "A positive protected reserve requires its intended provider.",
        });
});
// Derived audit/API projection only. Authoritative job, budget, artifact, asset,
// approval, command, and provider records remain normalized in their own stores.
export const bookGenerationEvidenceV1Schema = z
    .object({
    version: z.literal(1),
    generatedAt: z.string().datetime(),
    productionFamily: z.enum(["NARRATIVE", "RECIPE", "COMIC_CORE", "PICTURE_BOOK"]),
    job: z
        .object({
        jobId: ulidSchema,
        rootJobId: ulidSchema,
        retryOfJobId: ulidSchema.optional(),
    })
        .strict(),
    source: z
        .object({
        sourceSnapshotId: z.string().min(1).max(200),
        sourceHashSha256: sha256Schema,
    })
        .strict(),
    prompt: z
        .object({
        promptId: z.string().min(1).max(160),
        promptVersion: z.string().min(1).max(100),
        promptHashSha256: sha256Schema,
        schemaId: z.string().min(1).max(160),
        schemaVersion: z.string().min(1).max(100),
        schemaHashSha256: sha256Schema,
    })
        .strict(),
    budget: z
        .object({
        budgetId: z.string().min(1).max(200),
        reservationIds: z.array(z.string().min(1).max(200)).max(100),
    })
        .strict(),
    artifactRefs: z
        .array(z
        .object({
        artifactId: z.string().min(1).max(200),
        kind: z.string().min(1).max(100),
        objectVersionId: z.string().min(1).max(200),
        checksumSha256: sha256Schema,
    })
        .strict())
        .min(1)
        .max(500),
    approvalRefs: z.array(z.string().min(1).max(200)).max(100),
    idempotencyCommandId: z.string().min(1).max(200),
})
    .strict();
//# sourceMappingURL=book-generation.js.map